/**
 * ContractDetailPage — Phase 6a.18 per Frontend Spec §21.9.
 *
 * The per-contract drill-down: a mini-engagement view at the dedicated
 * route `/engagements/:engagementId/results/contracts/:contractId`
 * (Phase 6a.18b1 path correction — `/results/` segment inserted to
 * deconflict with the Phase 6a.8b edit route). Triggered
 * from the Sözleşmeler tab (Phase 6a.17) Detay button — the other 4
 * result tabs will gain navigation in Phase 6a.18b.
 *
 * Layout:
 *   • Breadcrumb (Engagement clientName → Sözleşmeler → contractId)
 *   • Hero header (contract identity + IBR + active flag emojis)
 *   • 5 inner tabs (shadcn Tabs, default "Özet"):
 *       1. Özet            — 4-card grid: initial, audit-period, lifetime, maturity
 *       2. Amortizasyon    — 12-column schedule with audit-period filter default
 *       3. Yevmiye         — per-contract JE list (reuses JECard/MonthGroup)
 *       4. Bayrak / Bilgi  — 5 active/inactive flag badges + provenance
 *       5. Sözleşme Olayları — ROU layers + event-type JE table
 *
 * 3 redirect cases (with toast feedback):
 *   • engagement missing in localStorage → /history
 *   • engagement.lastResult absent       → /engagements/:id/contracts
 *   • perContract entry not found        → /engagements/:id/results/contracts
 */

import { addMonths, format, isWithinInterval, parseISO } from "date-fns";
import Decimal from "decimal.js";
import { CheckCircle2, ChevronRight } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import { JournalYearlySummary } from "@/components/journal/JournalYearlySummary";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import { ReconciliationResultTable } from "./components/ReconciliationResultTable";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  computeAuditFigures,
  type AuditFigures,
} from "@/lib/auditFigures";
import { formatCurrency, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useHistoryStorage } from "@/services";
import type {
  AggregatedJournalEntry,
  AssetClass,
  ContractInput,
  EngagementHistoryEntry,
  JournalEntryType,
} from "@/types";
import type {
  CalculationResult,
  JournalEntry,
  ROULayer,
  SchedulePeriod,
} from "@/types/result";

import { MonthGroup } from "./tabs/journalEntryComponents";
import {
  CONTRACT_DETAIL_TABS,
  getSourceTabPath,
  type ContractDetailTab,
} from "./utils/contractDetailLink";

/**
 * MINI_FIX_H9 — sum backend Decimal strings without float precision loss.
 * Backend ships money as `toFixed(10)` strings (serialize.ts); accumulating
 * via `Number()` drifts at the kuruş over long schedules. We mirror the
 * backend's own arithmetic library (decimal.js, same package + version) so
 * the displayed totals match the per-row inputs exactly. Preserves the old
 * `|| 0` semantics: bad / missing / non-finite input → Decimal(0). Defined
 * inline to honour the H9 closed-list (no new lib file).
 */
function decSafe(v: unknown): Decimal {
  if (v === null || v === undefined) return new Decimal(0);
  try {
    const d = new Decimal(v as Decimal.Value);
    return d.isFinite() ? d : new Decimal(0);
  } catch {
    return new Decimal(0);
  }
}

/**
 * BUG-5 — audit-period-end figures for the "Denetim Dönemi Toplamları"
 * card. The whole tool exists to show, at the audit reporting date, the
 * numbers that get posted to the financial statements — so this card must
 * reflect the audit period END, NOT `perContract.year1*` (which backend
 * defines as the contract's first 12 schedule periods, i.e.
 * commencement + 12 months — wrong whenever commencement isn't aligned to
 * the audit year start).
 *
 *   • closingLiability = liabilitySplit.totalLiability (audit-end snap; the
 *     same value the engagement balance sheet aggregates). Falls back to
 *     the last schedule row's closingLiability for terminated contracts
 *     (liabilitySplit suppressed) or to year1ClosingLiability if no audit
 *     window is resolvable.
 *   • closingROU = closingROU of the schedule row CONTAINING auditPeriodEnd
 *     (period-start date convention → last row with date <= auditPeriodEnd).
 *   • interest / depreciation = sums over rows within
 *     [auditPeriodStart, auditPeriodEnd].
 */
function deriveAuditPeriodFigures(
  perContract: CalculationResult,
  auditPeriodStart: string,
  auditPeriodEnd: string,
  /**
   * MINI_FIX_CS Option A — when present, drives the FUNCTIONAL closing
   * liability/ROU to the reval-adjusted authoritative summary values
   * (ContractSummary.leaseLiabilityClosing / .rouAssetClosing) so they
   * tie to the engagement Balance Sheet on FX contracts. Falls back to
   * the schedule-derived af.* values when absent (back-compat for
   * unjoined / standalone-calc callers).
   */
  authoritativeClosings?:
    | { leaseLiabilityClosing: string; rouAssetClosing: string }
    | undefined,
): {
  // Native (contract currency). Names kept for back-compat with downstream
  // and tests that match these as the primary on single-currency engagements.
  closingLiability: string;
  closingROU: string;
  interest: string;
  depreciation: string;
  // MINI_FIX_CS — paired functional-currency variants (always returned;
  // identical to native when functional == contract currency).
  closingLiabilityFunctional: string;
  closingROUFunctional: string;
  interestFunctional: string;
  depreciationFunctional: string;
  /** The full paired figure set from the shared helper, for downstream use. */
  paired: AuditFigures;
} {
  const schedule = perContract.schedule ?? [];

  // Row containing the reporting date: last row with date <= auditPeriodEnd.
  let containing: SchedulePeriod | undefined;
  for (let i = schedule.length - 1; i >= 0; i--) {
    const row = schedule[i];
    if (row && row.date <= auditPeriodEnd) {
      containing = row;
      break;
    }
  }

  // Interest + depreciation summed over the calendar audit window.
  // MINI_FIX_H9 — accumulate via Decimal (not float) to keep the displayed
  // total decimal-exact against the per-row backend toFixed(10) strings.
  //
  // MINI_FIX_CP — bucket interest/depreciation by the ACCRUAL MONTH, not the
  // period-END `row.date`, to match the backend's accrual-window aggregation
  // (MINI_FIX_CO, IAS 1 §27-28). The accrual month is the calendar month being
  // accrued = period-start of the row = `row.date` minus one month (the engine
  // calls this `accrualMonthStart`; it is not serialized to the frontend
  // SchedulePeriod, so we derive it from row.date here). Without this, a lease
  // commenced before the audit year counts the prior December and misses the
  // audit-year December — the on-screen ÖZET would disagree with the
  // engagement P&L by one month of interest.
  let interest = new Decimal(0);
  let depreciation = new Decimal(0);
  for (const row of schedule) {
    const accrualMonthStart = format(
      addMonths(parseISO(row.date), -1),
      "yyyy-MM-dd",
    );
    if (
      accrualMonthStart >= auditPeriodStart &&
      accrualMonthStart <= auditPeriodEnd
    ) {
      interest = interest.plus(decSafe(row.interestExpense));
      depreciation = depreciation.plus(decSafe(row.depreciation));
    }
  }

  const lastRow = schedule[schedule.length - 1];

  // Closing liability: prefer the audit-end split (balance-sheet-correct),
  // then terminated/last-row fallback, then year1 as a last resort.
  const closingLiability =
    perContract.liabilitySplit?.totalLiability ??
    lastRow?.closingLiability ??
    perContract.year1ClosingLiability;

  // Closing ROU: the containing row's closing ROU; fall back to last row,
  // then year1.
  const closingROU =
    containing?.closingROU ??
    lastRow?.closingROU ??
    perContract.year1ClosingROU;

  // MINI_FIX_CG — Finding #5b. A contract terminated on/before the audit
  // period end has a ZERO end-of-period carrying amount per IFRS 16.46/.53(j)/.58:
  // the last-row closingLiability/closingROU is the in-period MOVEMENT (L_T /
  // Net_ROU_T) the termination booked, not a period-end BALANCE. Mirrors the
  // engine's `suppressForTermination` predicate (calculator.ts) and the
  // backend buildContractSummaries fix so the Detail card stays consistent
  // with the Contracts tab. The TerminationResultCard still shows L_T /
  // Net_ROU_T separately as termination-event figures; interest +
  // depreciation for the in-period sums stay unchanged (they DID occur in
  // the audit window).
  const terminatedByPeriodEnd =
    perContract.terminationResult !== undefined &&
    perContract.terminationResult.terminationDate <= auditPeriodEnd;

  // MINI_FIX_CS — also compute paired functional figures via the shared
  // helper. The helper's window logic is identical to the native window
  // above (same MINI_FIX_CP accrual rule), so the values reconcile by
  // construction. We keep the existing native code-path verbatim for
  // back-compat (the terminated/no-containing branches below preserve
  // historical behaviour the existing tests pin); the helper supplies
  // the paired functional values alongside.
  const paired = computeAuditFigures(
    perContract,
    auditPeriodStart,
    auditPeriodEnd,
  );

  // MINI_FIX_CS Option A — functional closing prefers the reval-adjusted
  // authoritative summary value (ContractSummary.leaseLiabilityClosing /
  // .rouAssetClosing — already in functional ccy, FX-revaluation-INCLUDED,
  // and tied to the engagement Balance Sheet via MINI_FIX_CG/CH). The raw
  // schedule-derived paired.closingLiability.functional is reval-EXCLUDED
  // and would drift from the Balance Sheet on FX contracts. The summary
  // closings are also already suppressed to "0.00" for terminated
  // contracts, so the existing terminatedByPeriodEnd branch still works.
  const closingLiabilityFunctional =
    authoritativeClosings?.leaseLiabilityClosing ??
    paired.closingLiability.functional;
  const closingROUFunctional =
    authoritativeClosings?.rouAssetClosing ??
    paired.closingROU.functional;

  // If we couldn't resolve an audit window at all (no row <= end), fall
  // back wholesale to year1* so the card never renders blank.
  if (!containing) {
    return {
      closingLiability: terminatedByPeriodEnd
        ? "0.00"
        : perContract.year1ClosingLiability,
      closingROU: terminatedByPeriodEnd
        ? "0.00"
        : perContract.year1ClosingROU,
      interest: perContract.year1Interest,
      depreciation: perContract.year1Depreciation,
      closingLiabilityFunctional: terminatedByPeriodEnd
        ? "0.00"
        : closingLiabilityFunctional,
      closingROUFunctional: terminatedByPeriodEnd
        ? "0.00"
        : closingROUFunctional,
      interestFunctional: paired.interest.functional,
      depreciationFunctional: paired.depreciation.functional,
      paired,
    };
  }

  return {
    closingLiability: terminatedByPeriodEnd ? "0.00" : closingLiability,
    closingROU: terminatedByPeriodEnd ? "0.00" : closingROU,
    interest: interest.toFixed(2),
    depreciation: depreciation.toFixed(2),
    closingLiabilityFunctional: terminatedByPeriodEnd
      ? "0.00"
      : closingLiabilityFunctional,
    closingROUFunctional: terminatedByPeriodEnd
      ? "0.00"
      : closingROUFunctional,
    interestFunctional: paired.interest.functional,
    depreciationFunctional: paired.depreciation.functional,
    paired,
  };
}

/**
 * Whitelist the `?tab=` query value against the canonical set. Invalid
 * or missing values fall back to "summary" so a malformed deep-link
 * never strands the auditor on an empty tab.
 */
function resolveInitialTab(raw: string | null): ContractDetailTab {
  if (!raw) return "summary";
  return (CONTRACT_DETAIL_TABS as string[]).includes(raw)
    ? (raw as ContractDetailTab)
    : "summary";
}

const EVENT_TYPES: ReadonlySet<JournalEntryType> = new Set([
  "LEASE_MODIFICATION_INCREASE",
  "LEASE_MODIFICATION_DECREASE_STEP1",
  "LEASE_MODIFICATION_DECREASE_STEP2",
  "LEASE_TERMINATION",
  "INDEX_REMEASUREMENT",
  "FX_REMEASUREMENT",
  "LEASE_RECLASSIFICATION",
  "AUDIT_ADJUSTMENT",
]);

const PER_CONTRACT_JE_TYPES: JournalEntryType[] = [
  "INITIAL_RECOGNITION",
  "MONTHLY_INTEREST",
  "MONTHLY_DEPRECIATION",
  "LEASE_PAYMENT",
  "LEASE_MODIFICATION_INCREASE",
  // BB3 — mirror the engagement-level filter (see ALL_JE_TYPES in
  // JournalEntriesTab.tsx) so backend's IFRS 16 §45 not-separate
  // scope-increase entries are reachable from the per-contract drill-down.
  "LEASE_MODIFICATION_INCREASE_NOT_SEPARATE",
  "LEASE_MODIFICATION_DECREASE_STEP1",
  "LEASE_MODIFICATION_DECREASE_STEP2",
  "LEASE_TERMINATION",
  "INDEX_REMEASUREMENT",
  "LEASE_RECLASSIFICATION",
  "FX_REMEASUREMENT",
  // BB3 — backend's period-end IAS 21 revaluation rows.
  "FX_REVALUATION",
  "VARIABLE_PAYMENT",
  "AUDIT_ADJUSTMENT",
];

export function ContractDetailPage() {
  const { engagementId: rawEngagementId, contractId: rawContractId } =
    useParams<{ engagementId: string; contractId: string }>();
  const engagementId = rawEngagementId
    ? decodeURIComponent(rawEngagementId)
    : "";
  const contractId = rawContractId ? decodeURIComponent(rawContractId) : "";

  const navigate = useNavigate();
  const { t } = useTranslation();
  const historyStorage = useHistoryStorage();

  // Phase 6a.18b — source-aware initial tab. Each surface that links
  // here (Yevmiye JE card → `?tab=journal`, WarningsBanner → no query,
  // etc.) passes the appropriate intent through the URL. Unknown or
  // missing values fall back to "summary" — the auditor never lands on
  // a blank or broken tab.
  const [searchParams] = useSearchParams();
  const initialTab = resolveInitialTab(searchParams.get("tab"));

  const [engagement, setEngagement] =
    React.useState<EngagementHistoryEntry | null>(null);
  const [loadState, setLoadState] = React.useState<
    "loading" | "loaded" | "missing"
  >("loading");

  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!engagementId) {
        if (!cancelled) setLoadState("missing");
        return;
      }
      const e = await historyStorage.getEngagement(engagementId);
      if (cancelled) return;
      if (!e) {
        setLoadState("missing");
      } else {
        setEngagement(e);
        setLoadState("loaded");
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [engagementId, historyStorage]);

  React.useEffect(() => {
    if (loadState === "missing") {
      toast.error(t("contractDetail.error.engagementNotFound"));
      navigate("/history", { replace: true });
      return;
    }
    if (loadState === "loaded" && engagement && !engagement.lastResult) {
      toast.error(t("contractDetail.error.notCalculated"));
      navigate(`/engagements/${engagement.id}/contracts`, { replace: true });
      return;
    }
    if (loadState === "loaded" && engagement?.lastResult) {
      const found = engagement.lastResult.perContract.find(
        (pc) => pc.contractId === contractId,
      );
      if (!found) {
        toast.error(
          t("contractDetail.error.contractNotFound", { contractId }),
        );
        navigate(`/engagements/${engagement.id}/results/contracts`, {
          replace: true,
        });
      }
    }
  }, [loadState, engagement, contractId, navigate, t]);

  if (loadState !== "loaded" || !engagement?.lastResult) {
    return null;
  }
  const perContract = engagement.lastResult.perContract.find(
    (pc) => pc.contractId === contractId,
  );
  if (!perContract) return null;

  // Lookup the original contract input (kept on the engagement entry)
  // for fields not echoed in `perContract`: discountRateAnnual,
  // assetClass, description. Defensive — older entries may have a
  // `null` slot.
  const contractInput: ContractInput | undefined =
    engagement.contracts
      .filter((c): c is ContractInput => c !== null)
      .find((c) => c.contractId === contractId) ?? undefined;

  const annualRate = contractInput?.discountRateAnnual ?? 0;
  const description = contractInput?.description;
  const contractSummary =
    engagement.lastResult.aggregations.contractSummaries.find(
      (cs) => cs.contractId === contractId,
    );
  // The engagement-layer `EngagementContractInput` adds `assetClass` to
  // the base `ContractInput`; the localStorage type widens it back to
  // the base, so read from `contractSummary` (always populated by the
  // backend) and fall back to the runtime-typed input if needed.
  const assetClass =
    contractSummary?.assetClass ??
    ((contractInput as unknown as { assetClass?: AssetClass })?.assetClass);

  return (
    <div
      className="space-y-4 max-w-7xl mx-auto"
      data-testid="contract-detail-page"
    >
      <Breadcrumb
        engagementId={engagement.id}
        engagementLabel={engagement.clientName}
        contractId={contractId}
        sourceTab={initialTab}
      />

      <HeroHeader
        contractId={contractId}
        description={description}
        assetClass={assetClass}
        commencementDate={perContract.commencementDate}
        termMonths={perContract.leaseTermMonths}
        currency={perContract.currency}
        annualRate={annualRate}
        monthlyRate={perContract.periodicRateMonthly}
        contractSummary={contractSummary}
      />

      <TabSwitcher
        perContract={perContract}
        engagement={engagement}
        contractId={contractId}
        annualRate={annualRate}
        contractSummary={contractSummary}
        initialTab={initialTab}
      />
    </div>
  );
}

type TabKey =
  | "summary"
  | "amortization"
  | "journal"
  | "reconciliation"
  | "metadata"
  | "events";

interface TabSwitcherProps {
  perContract: CalculationResult;
  engagement: EngagementHistoryEntry;
  contractId: string;
  annualRate: number;
  contractSummary:
    | {
        isMultiCurrency: boolean;
        hasModifications: boolean;
        hasTermination: boolean;
        hasIndexLinkage: boolean;
        hasPriorReconciliation: boolean;
        // MINI_FIX_CS Option A — authoritative reval-adjusted closings
        // (functional ccy). Threaded to SummarySection so the ÖZET audit-
        // period card can tie to the engagement Balance Sheet via
        // ContractSummary.leaseLiabilityClosing / .rouAssetClosing
        // (post-MINI_FIX_CG/CH).
        leaseLiabilityClosing: string;
        rouAssetClosing: string;
      }
    | undefined;
  /**
   * Phase 6a.18b — source-aware default tab passed from the page,
   * resolved from the `?tab=` URL query parameter with whitelist
   * validation. The switcher is uncontrolled after first render —
   * clicks update the local `active` state without writing back to the
   * URL (the query parameter is intent-on-arrival, not live state).
   */
  initialTab: TabKey;
}

/**
 * Lightweight in-house tab switcher. The shadcn `Tabs` primitive uses
 * Radix internals that are brittle under jsdom click dispatch, so this
 * page sticks with simple `useState` + conditional rendering. The
 * pinned chrome (rounded list + active highlight) is preserved.
 */
function TabSwitcher({
  perContract,
  engagement,
  contractId,
  annualRate,
  contractSummary,
  initialTab,
}: TabSwitcherProps) {
  const { t } = useTranslation();
  const [active, setActive] = React.useState<TabKey>(initialTab);
  const tabs: { key: TabKey; label: string }[] = [
    { key: "summary", label: t("contractDetail.tabs.summary") },
    { key: "amortization", label: t("contractDetail.tabs.amortization") },
    {
      key: "journal",
      label: t("contractDetail.tabs.journalEntries", {
        count: perContract.journalEntries.length,
      }),
    },
    // Phase 6a.20e — Mutabakat tab only appears when the contract has a
    // reconciliation result. Auditors who don't use prior year
    // reconciliation never see the tab; no visual artifacts in the strip.
    ...(perContract.reconciliation
      ? [
          {
            key: "reconciliation" as const,
            label: t(
              "engagement.contract.priorReconciliation.result.tabLabel",
            ),
          },
        ]
      : []),
    { key: "metadata", label: t("contractDetail.tabs.metadata") },
    { key: "events", label: t("contractDetail.tabs.events") },
  ];
  return (
    <div className="w-full">
      <div
        className="inline-flex items-center gap-1 rounded-lg bg-muted p-1 mb-4"
        role="tablist"
        data-testid="contract-detail-tabs"
      >
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active === tab.key}
            data-testid={`tab-trigger-${tab.key}`}
            data-active={active === tab.key ? "true" : "false"}
            onClick={() => setActive(tab.key)}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              active === tab.key
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {active === "summary" && (
        <SummarySection
          perContract={perContract}
          annualRate={annualRate}
          functionalCurrency={engagement.functionalCurrency}
          auditPeriodStart={engagement.auditPeriodStart}
          auditPeriodEnd={engagement.auditPeriodEnd}
          authoritativeClosings={
            // MINI_FIX_CS Option A — the reval-adjusted (functional) closings
            // for tie-to-Balance-Sheet on the Kapanış Yükümlülük / Kapanış
            // ROU rows. Undefined when contractSummary is unjoined.
            contractSummary
              ? {
                  leaseLiabilityClosing: contractSummary.leaseLiabilityClosing,
                  rouAssetClosing: contractSummary.rouAssetClosing,
                }
              : undefined
          }
        />
      )}
      {active === "amortization" && (
        <AmortizationSection
          schedule={perContract.schedule}
          currency={perContract.currency}
          functionalCurrency={perContract.functionalCurrency}
          isFxContract={perContract.isFxContract === true}
          auditPeriodStart={engagement.auditPeriodStart}
          auditPeriodEnd={engagement.auditPeriodEnd}
          terminationDate={perContract.terminationResult?.terminationDate}
        />
      )}
      {active === "journal" && (
        <JESection
          journalEntries={perContract.journalEntries}
          contractId={contractId}
          currency={perContract.currency}
          auditPeriodStart={engagement.auditPeriodStart}
          auditPeriodEnd={engagement.auditPeriodEnd}
        />
      )}
      {active === "reconciliation" && perContract.reconciliation && (
        <ReconciliationResultTable
          reconciliation={perContract.reconciliation}
          contractCurrency={perContract.currency}
          functionalCurrency={engagement.functionalCurrency}
        />
      )}
      {active === "metadata" && (
        <MetadataSection
          contract={perContract}
          contractSummary={contractSummary}
        />
      )}
      {active === "events" && (
        <EventsSection
          rouLayers={perContract.rouLayers}
          journalEntries={perContract.journalEntries}
          currency={perContract.currency}
        />
      )}
    </div>
  );
}

// ─── Breadcrumb ────────────────────────────────────────────────────────────

function Breadcrumb({
  engagementId,
  engagementLabel,
  contractId,
  sourceTab,
}: {
  engagementId: string;
  engagementLabel: string;
  contractId: string;
  /**
   * Phase 6a.18b1.1 — the inner-tab key resolved from `?tab=` (or its
   * fallback). Drives both the middle link's label and its href so the
   * auditor lands on the engagement-level tab they drilled from.
   */
  sourceTab: ContractDetailTab;
}) {
  const { t } = useTranslation();
  // Phase 6a.18b1.1 — source-aware middle link. Plain navigation: the
  // click does NOT modify or remove the page's `?tab=` query param.
  const sourceTabLabel = t(
    `contractDetail.breadcrumb.source.${sourceTab}` as never,
  ) as string;
  const sourceTabPath = getSourceTabPath(engagementId, sourceTab);
  return (
    <nav
      className="flex items-center gap-2 text-sm text-muted-foreground py-2 border-b"
      data-testid="contract-detail-breadcrumb"
    >
      <Link
        to={`/engagements/${engagementId}/results`}
        className="hover:underline hover:text-foreground"
        data-testid="breadcrumb-engagement"
      >
        {engagementLabel || t("contractDetail.breadcrumb.engagement")}
      </Link>
      <ChevronRight className="h-3 w-3" />
      <Link
        to={sourceTabPath}
        className="hover:underline hover:text-foreground"
        data-testid="breadcrumb-source"
      >
        {sourceTabLabel}
      </Link>
      <ChevronRight className="h-3 w-3" />
      <span
        className="text-foreground font-medium"
        data-testid="breadcrumb-contract"
      >
        {contractId}
      </span>
    </nav>
  );
}

// ─── Hero Header ───────────────────────────────────────────────────────────

interface HeroHeaderProps {
  contractId: string;
  description?: string;
  assetClass?: AssetClass;
  commencementDate: string;
  termMonths: number;
  currency: string;
  annualRate: number;
  monthlyRate: string;
  contractSummary:
    | {
        isMultiCurrency: boolean;
        hasModifications: boolean;
        hasTermination: boolean;
        hasIndexLinkage: boolean;
        hasPriorReconciliation: boolean;
      }
    | undefined;
}

function HeroHeader({
  contractId,
  description,
  assetClass,
  commencementDate,
  termMonths,
  currency,
  annualRate,
  monthlyRate,
  contractSummary,
}: HeroHeaderProps) {
  const { t } = useTranslation();
  const start = parseISO(commencementDate);
  const startDisplay = Number.isNaN(start.getTime())
    ? commencementDate
    : format(start, "dd.MM.yyyy");
  const endDisplay = Number.isNaN(start.getTime())
    ? "—"
    : format(addMonths(start, termMonths), "dd.MM.yyyy");
  const monthlyPct = (Number(monthlyRate) || 0) * 100;
  const annualLabel =
    typeof annualRate === "number" ? annualRate.toFixed(2) : "0.00";
  const assetClassLabel = assetClass
    ? (t(`form.assetClass.${assetClass}` as never) as string)
    : "—";

  return (
    <Card className="p-5 glow-card" data-testid="contract-detail-hero">
      <h1
        className="text-xl font-semibold"
        data-testid="contract-detail-hero-title"
      >
        📄 {contractId}
        {description ? ` — ${description}` : ""}
      </h1>
      <p
        className="mt-1 text-sm text-muted-foreground"
        data-testid="contract-detail-hero-meta"
      >
        {assetClassLabel} ·{" "}
        {t("contractDetail.hero.dateRange", {
          start: startDisplay,
          end: endDisplay,
        })}{" "}
        ({t("contractDetail.hero.term", { months: termMonths })}) · {currency}{" "}
        ·{" "}
        {t("contractDetail.hero.ibr", {
          annual: annualLabel,
          monthly: monthlyPct.toFixed(4),
        })}
      </p>
      <div
        className="mt-2 flex gap-2 text-lg"
        data-testid="contract-detail-hero-flags"
      >
        {contractSummary ? (
          <HeroFlags summary={contractSummary} />
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </div>
    </Card>
  );
}

function HeroFlags({
  summary,
}: {
  summary: NonNullable<HeroHeaderProps["contractSummary"]>;
}) {
  const { t } = useTranslation();
  const flags = [
    {
      active: summary.isMultiCurrency,
      emoji: "🌐",
      label: t("contracts.flag.multiCurrency"),
      key: "multiCurrency",
    },
    {
      active: summary.hasModifications,
      emoji: "✏️",
      label: t("contracts.flag.modification"),
      key: "modification",
    },
    {
      active: summary.hasTermination,
      emoji: "⏹",
      label: t("contracts.flag.termination"),
      key: "termination",
    },
    {
      active: summary.hasIndexLinkage,
      emoji: "📊",
      label: t("contracts.flag.indexLinkage"),
      key: "indexLinkage",
    },
    {
      active: summary.hasPriorReconciliation,
      emoji: "🔄",
      label: t("contracts.flag.priorReconciliation"),
      key: "priorReconciliation",
    },
  ];
  const active = flags.filter((f) => f.active);
  if (active.length === 0) {
    // Phase BB5-6 — replace bare em-dash with a softer, italic "Olay yok" label.
    return (
      <span
        className="text-muted-foreground/60 text-xs italic"
        data-testid="hero-flags-none"
      >
        {t("contractDetail.events.noFlags")}
      </span>
    );
  }
  return (
    <>
      {active.map((f) => (
        <span key={f.key} title={f.label} data-testid={`hero-flag-${f.key}`}>
          {f.emoji}
        </span>
      ))}
    </>
  );
}

// ─── Section 1: Summary (4-card grid) ──────────────────────────────────────

interface SummarySectionProps {
  perContract: CalculationResult;
  annualRate: number;
  functionalCurrency: string;
  auditPeriodStart: string;
  auditPeriodEnd: string;
  /**
   * MINI_FIX_CS Option A — the authoritative reval-adjusted closings
   * (functional ccy) from the engagement aggregator's ContractSummary.
   * Drives the ÖZET "Denetim Dönemi → Kapanış Yükümlülüğü / Kapanış ROU"
   * functional values so they tie to the engagement Balance Sheet.
   * Native values still come from the schedule via auditFigures.
   */
  authoritativeClosings:
    | { leaseLiabilityClosing: string; rouAssetClosing: string }
    | undefined;
}

function SummarySection({
  perContract,
  annualRate,
  functionalCurrency,
  auditPeriodStart,
  auditPeriodEnd,
  authoritativeClosings,
}: SummarySectionProps) {
  const { t } = useTranslation();
  const monthlyPct = (Number(perContract.periodicRateMonthly) || 0) * 100;
  const cur = perContract.currency;
  const liabSplit = perContract.liabilitySplit;
  // BUG-5 — audit-period-END figures (what gets posted at period close),
  // replacing the commencement-anchored year1* values.
  // MINI_FIX_CS Option A — pass authoritativeClosings (ContractSummary)
  // through so deriveAuditPeriodFigures returns the reval-adjusted
  // closings on the functional side, ties-to-Balance-Sheet.
  const auditFigures = deriveAuditPeriodFigures(
    perContract,
    auditPeriodStart,
    auditPeriodEnd,
    authoritativeClosings,
  );
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="contract-detail-summary"
    >
      <SummaryCard
        title={t("contractDetail.summary.initial.title")}
        testId="summary-initial"
        rows={[
          {
            // MINI_FIX_CS — functional primary, native sub-line.
            label: t("contractDetail.summary.initial.liability"),
            value: formatCurrency(
              auditFigures.paired.initialLiability.functional,
              functionalCurrency,
            ),
            native: formatCurrency(
              auditFigures.paired.initialLiability.native,
              cur,
            ),
          },
          {
            label: t("contractDetail.summary.initial.rou"),
            value: formatCurrency(
              auditFigures.paired.initialROU.functional,
              functionalCurrency,
            ),
            native: formatCurrency(
              auditFigures.paired.initialROU.native,
              cur,
            ),
          },
          {
            label: t("contractDetail.summary.initial.monthlyRate"),
            value: `%${monthlyPct.toFixed(4)}`,
          },
          {
            label: t("contractDetail.summary.initial.annualRate"),
            value: `%${annualRate.toFixed(2)}`,
          },
          {
            label: t("contractDetail.summary.initial.termMonths"),
            value: t("contractDetail.hero.term", {
              months: perContract.leaseTermMonths,
            }),
          },
        ]}
      />
      <SummaryCard
        title={t("contractDetail.summary.auditPeriod.title")}
        testId="summary-audit-period"
        rows={[
          {
            // MINI_FIX_CS — functional uses the accrual-window functional
            // figure (matches engagement P&L per-contract breakdown by
            // construction — same accrual window, same *Functional cols).
            label: t("contractDetail.summary.auditPeriod.interest"),
            value: formatCurrency(
              auditFigures.interestFunctional,
              functionalCurrency,
            ),
            native: formatCurrency(auditFigures.interest, cur),
          },
          {
            label: t("contractDetail.summary.auditPeriod.depreciation"),
            value: formatCurrency(
              auditFigures.depreciationFunctional,
              functionalCurrency,
            ),
            native: formatCurrency(auditFigures.depreciation, cur),
          },
          {
            label: t("contractDetail.summary.auditPeriod.closingLiability"),
            value: formatCurrency(
              auditFigures.closingLiabilityFunctional,
              functionalCurrency,
            ),
            native: formatCurrency(auditFigures.closingLiability, cur),
          },
          {
            label: t("contractDetail.summary.auditPeriod.closingROU"),
            value: formatCurrency(
              auditFigures.closingROUFunctional,
              functionalCurrency,
            ),
            native: formatCurrency(auditFigures.closingROU, cur),
          },
        ]}
      />
      <SummaryCard
        title={t("contractDetail.summary.lifetime.title")}
        testId="summary-lifetime"
        rows={[
          {
            label: t("contractDetail.summary.lifetime.interest"),
            value: formatCurrency(
              auditFigures.paired.totalInterest.functional,
              functionalCurrency,
            ),
            native: formatCurrency(
              auditFigures.paired.totalInterest.native,
              cur,
            ),
          },
          {
            label: t("contractDetail.summary.lifetime.payments"),
            value: formatCurrency(
              auditFigures.paired.totalPayments.functional,
              functionalCurrency,
            ),
            native: formatCurrency(
              auditFigures.paired.totalPayments.native,
              cur,
            ),
          },
          {
            label: t("contractDetail.summary.lifetime.depreciation"),
            value: formatCurrency(
              auditFigures.paired.totalDepreciation.functional,
              functionalCurrency,
            ),
            native: formatCurrency(
              auditFigures.paired.totalDepreciation.native,
              cur,
            ),
          },
          {
            // Variable payments stay native (engine does not expose a
            // functional aggregate); display in contract ccy in both slots
            // — the sub-line auto-suppresses when they match (single ccy)
            // and shows the same value in the foreign-ccy case.
            label: t("contractDetail.summary.lifetime.variablePayments"),
            value: formatCurrency(perContract.variablePaymentsTotal, cur),
          },
        ]}
      />
      <SummaryCard
        title={t("contractDetail.summary.maturity.title")}
        testId="summary-maturity"
        rows={
          liabSplit
            ? (() => {
                // MINI_FIX_CS Option A — maturity TOTAL functional anchored to
                // the reval-adjusted authoritative ContractSummary closing
                // (ties to Balance Sheet). ST functional stays schedule-
                // derived (forward-rate-shaped per IAS 1 §69-76 — the FX
                // disclosure note below explains this). LT = TOTAL − ST so
                // the row self-checks AND ties (Kısa + Uzun = Toplam =
                // Kapanış Yükümlülüğü).
                const totalFn =
                  authoritativeClosings?.leaseLiabilityClosing ??
                  auditFigures.paired.totalLiability?.functional ??
                  liabSplit.totalLiability;
                const stFn =
                  auditFigures.paired.shortTerm?.functional ??
                  liabSplit.shortTerm;
                const totalNum = Number(totalFn);
                const stNum = Number(stFn);
                const ltFn =
                  Number.isFinite(totalNum) && Number.isFinite(stNum)
                    ? (totalNum - stNum).toFixed(2)
                    : (auditFigures.paired.longTerm?.functional ??
                      liabSplit.longTerm);
                return [
                  {
                    label: t("contractDetail.summary.maturity.reportingDate"),
                    value: format(
                      parseISO(liabSplit.reportingDate),
                      "dd.MM.yyyy",
                    ),
                  },
                  {
                    label: t("contractDetail.summary.maturity.shortTerm"),
                    value: formatCurrency(stFn, functionalCurrency),
                    native: formatCurrency(liabSplit.shortTerm, cur),
                  },
                  {
                    label: t("contractDetail.summary.maturity.longTerm"),
                    value: formatCurrency(ltFn, functionalCurrency),
                    native: formatCurrency(liabSplit.longTerm, cur),
                  },
                  {
                    label: t("contractDetail.summary.maturity.total"),
                    value: formatCurrency(totalFn, functionalCurrency),
                    native: formatCurrency(liabSplit.totalLiability, cur),
                  },
                ];
              })()
            : [
                {
                  label: t("contractDetail.summary.maturity.total"),
                  value: "—",
                },
              ]
        }
        footer={
          // MINI_FIX_CP — disclosure note for FX contracts only. IAS 21
          // §23(a) closing-rate translation of the TOTAL is exact; the
          // ST/LT split is shaped by forward payment-date rates per
          // IAS 1 §69-76 → presentation/estimate characteristic, not
          // a calculation error. Same text appears in the Excel ÖZET.
          perContract.isFxContract === true &&
          perContract.liabilitySplit !== undefined ? (
            <p
              className="text-xs italic text-muted-foreground mt-3"
              data-testid="summary-maturity-fx-note"
            >
              ℹ Döviz cinsi sözleşmelerde vade ayrımı (kısa/uzun) hakkında:
              Toplam kira yükümlülüğü, raporlama tarihindeki kapanış kuruyla
              fonksiyonel para birimine çevrilir (IAS 21 §23(a)) ve kesindir.
              Kısa vade tutarı, gelecek 12 ayın anapara taksitlerinin her
              birinin kendi ödeme-tarihi kuruyla çevrilmesiyle; uzun vade ise
              (toplam − kısa vade) olarak hesaplanır (IAS 1 §69-76). Bu
              nedenle kısa/uzun vade ayrımının fonksiyonel para birimi
              tutarları, ileriye dönük kur varsayımlarına duyarlıdır. Toplam
              yükümlülük her durumda doğrudur; ayrımın mutlak tutarları bir
              sunum/tahmin unsurudur, hesaplama hatası değildir.
            </p>
          ) : undefined
        }
      />
      {functionalCurrency !== cur && (
        <p className="md:col-span-2 text-xs italic text-muted-foreground">
          {`Sözleşme para birimi: ${cur} · Functional: ${functionalCurrency}`}
        </p>
      )}
      {perContract.remeasurementEvents &&
        perContract.remeasurementEvents.length > 0 && (
          <div className="md:col-span-2">
            <RemeasurementEventsCard
              events={perContract.remeasurementEvents}
              currency={cur}
              functionalCurrency={functionalCurrency}
              isFxContract={functionalCurrency !== cur}
            />
          </div>
        )}
      {perContract.terminationResult && (
        <div className="md:col-span-2">
          <TerminationResultCard
            result={perContract.terminationResult}
            currency={cur}
            functionalCurrency={functionalCurrency}
            isFxContract={functionalCurrency !== cur}
          />
        </div>
      )}
    </div>
  );
}

// ─── Remeasurement events card (Phase 6a.20d, IFRS 16 §42(b)) ─────────────

interface RemeasurementEventsCardProps {
  events: NonNullable<CalculationResult["remeasurementEvents"]>;
  currency: string;
  functionalCurrency: string;
  isFxContract: boolean;
}

function RemeasurementEventsCard({
  events,
  currency,
  functionalCurrency,
  isFxContract,
}: RemeasurementEventsCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="p-5 glow-card" data-testid="remeasurement-events-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t("engagement.contract.indexLinkage.result.title")}
        </h4>
        <span className="text-xs text-muted-foreground">
          {t("engagement.contract.indexLinkage.result.count", {
            count: events.length,
          })}
        </span>
      </div>
      <div className="space-y-4">
        {events.map((ev, idx) => {
          const adjustment = Number(ev.adjustment) || 0;
          const adjustmentColor =
            adjustment > 0
              ? "text-emerald-700 dark:text-emerald-300"
              : adjustment < 0
                ? "text-red-700 dark:text-red-300"
                : "text-foreground";
          const gain = Number(ev.gainOnRemeasurement ?? 0) || 0;
          return (
            <div
              key={`${ev.period}-${ev.date}-${idx}`}
              className="rounded-md border border-muted-foreground/15 p-3"
              data-testid={`remeasurement-event-${idx}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">
                  {t(
                    "engagement.contract.indexLinkage.result.eventTitle",
                    {
                      index: idx + 1,
                      period: ev.period,
                      date: ev.date,
                    },
                  )}
                </p>
                {gain > 0 && (
                  <Badge
                    className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                    data-testid={`remeasurement-event-gain-badge-${idx}`}
                  >
                    {t(
                      "engagement.contract.indexLinkage.result.labels.gainOnRemeasurement",
                    )}
                  </Badge>
                )}
              </div>
              <dl className="space-y-1.5 text-sm">
                <div className="flex justify-between items-baseline gap-3">
                  <dt className="text-muted-foreground">
                    {t(
                      "engagement.contract.indexLinkage.result.labels.previousPayment",
                    )}{" "}
                    →{" "}
                    {t(
                      "engagement.contract.indexLinkage.result.labels.newPayment",
                    )}
                  </dt>
                  <dd className="font-mono tabular-nums font-medium">
                    {formatCurrency(ev.previousPayment, currency)}
                    {" → "}
                    {formatCurrency(ev.newPayment, currency)}
                  </dd>
                </div>
                <div className="flex justify-between items-baseline gap-3">
                  <dt className="text-muted-foreground">
                    {t(
                      "engagement.contract.indexLinkage.result.labels.liabilityBefore",
                    )}{" "}
                    →{" "}
                    {t(
                      "engagement.contract.indexLinkage.result.labels.liabilityAfter",
                    )}
                  </dt>
                  <dd className="font-mono tabular-nums font-medium">
                    {formatCurrency(ev.liabilityBefore, currency)}
                    {" → "}
                    {formatCurrency(ev.liabilityAfter, currency)}
                  </dd>
                </div>
                <div className="flex justify-between items-baseline gap-3">
                  <dt className="text-sm font-medium">
                    {t(
                      "engagement.contract.indexLinkage.result.labels.adjustment",
                    )}
                  </dt>
                  <dd
                    className={cn(
                      "font-mono tabular-nums font-semibold",
                      adjustmentColor,
                    )}
                    data-testid={`remeasurement-event-adjustment-${idx}`}
                  >
                    {formatCurrency(ev.adjustment, currency)}
                  </dd>
                </div>
                {isFxContract && ev.adjustmentFunctional !== undefined && (
                  <div className="flex justify-between items-baseline gap-3">
                    <dt className="text-xs text-muted-foreground">
                      {t(
                        "engagement.contract.indexLinkage.result.labels.adjustmentFunctional",
                      )}
                      {ev.resetDateRate !== undefined && (
                        <span className="ml-1">
                          {t(
                            "engagement.contract.indexLinkage.result.rateLabel",
                            { rate: ev.resetDateRate },
                          )}
                        </span>
                      )}
                    </dt>
                    <dd
                      className="font-mono tabular-nums text-xs"
                      data-testid={`remeasurement-event-adjustment-functional-${idx}`}
                    >
                      {formatCurrency(
                        ev.adjustmentFunctional,
                        functionalCurrency,
                      )}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between items-baseline gap-3">
                  <dt className="text-muted-foreground">
                    {t(
                      "engagement.contract.indexLinkage.result.labels.rouBefore",
                    )}{" "}
                    →{" "}
                    {t(
                      "engagement.contract.indexLinkage.result.labels.rouAfter",
                    )}
                  </dt>
                  <dd className="font-mono tabular-nums font-medium">
                    {formatCurrency(ev.rouBefore, currency)}
                    {" → "}
                    {formatCurrency(ev.rouAfter, currency)}
                  </dd>
                </div>
                {gain > 0 && (
                  <div className="flex justify-between items-baseline gap-3 pt-1 border-t">
                    <dt className="text-xs text-emerald-700 dark:text-emerald-300">
                      {t(
                        "engagement.contract.indexLinkage.result.labels.gainOnRemeasurement",
                      )}
                    </dt>
                    <dd
                      className="font-mono tabular-nums text-xs text-emerald-700 dark:text-emerald-300"
                      data-testid={`remeasurement-event-gain-${idx}`}
                    >
                      {formatCurrency(ev.gainOnRemeasurement!, currency)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Termination result card (Phase 6a.20c, IFRS 16 §46(a)) ───────────────

interface TerminationResultCardProps {
  result: NonNullable<CalculationResult["terminationResult"]>;
  currency: string;
  functionalCurrency: string;
  isFxContract: boolean;
}

function TerminationResultCard({
  result,
  currency,
  functionalCurrency,
  isFxContract,
}: TerminationResultCardProps) {
  const { t } = useTranslation();
  const gainOrLoss = Number(result.gainOrLoss) || 0;

  const badgeKind: "gain" | "loss" | "neutral" =
    gainOrLoss > 0 ? "gain" : gainOrLoss < 0 ? "loss" : "neutral";
  const badgeClass =
    badgeKind === "gain"
      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
      : badgeKind === "loss"
        ? "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300"
        : "bg-muted text-muted-foreground";
  const badgeLabel = t(
    badgeKind === "gain"
      ? "engagement.contract.termination.result.gainBadge"
      : badgeKind === "loss"
        ? "engagement.contract.termination.result.lossBadge"
        : "engagement.contract.termination.result.neutralBadge",
  );

  const rows: Array<{ label: string; value: string }> = [
    {
      label: t(
        "engagement.contract.termination.result.labels.liabilityAtTermination",
      ),
      value: formatCurrency(result.liabilityAtTermination, currency),
    },
    {
      label: t(
        "engagement.contract.termination.result.labels.netROUAtTermination",
      ),
      value: formatCurrency(result.netROUAtTermination, currency),
    },
    {
      label: t(
        "engagement.contract.termination.result.labels.terminationPenalty",
      ),
      value: formatCurrency(result.terminationPenalty, currency),
    },
    {
      label: t(
        "engagement.contract.termination.result.labels.restorationCostsActual",
      ),
      value: formatCurrency(result.restorationCostsActual, currency),
    },
  ];

  return (
    <Card className="p-5 glow-card" data-testid="termination-result-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t("engagement.contract.termination.result.title")}
        </h4>
        <Badge
          className={cn("text-xs px-2 py-0.5", badgeClass)}
          data-testid="termination-result-badge"
          data-kind={badgeKind}
        >
          {badgeLabel}
        </Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        {t("engagement.contract.termination.result.summary", {
          date: result.terminationDate,
          period: result.period,
        })}
      </p>
      <dl className="space-y-1.5 text-sm">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex justify-between items-baseline gap-3"
          >
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd className="font-mono tabular-nums font-medium">{r.value}</dd>
          </div>
        ))}
        <div className="flex justify-between items-baseline gap-3 pt-2 border-t">
          <dt className="text-sm font-medium">
            {t("engagement.contract.termination.result.labels.gainOrLoss")}
          </dt>
          <dd
            className="font-mono tabular-nums font-semibold"
            data-testid="termination-result-gainOrLoss"
          >
            {formatCurrency(result.gainOrLoss, currency)}
          </dd>
        </div>
        {isFxContract && result.gainOrLossFunctional !== undefined && (
          <div className="flex justify-between items-baseline gap-3">
            <dt className="text-xs text-muted-foreground">
              {t(
                "engagement.contract.termination.result.labels.gainOrLossFunctional",
              )}
            </dt>
            <dd
              className="font-mono tabular-nums text-xs"
              data-testid="termination-result-gainOrLossFunctional"
            >
              {formatCurrency(
                result.gainOrLossFunctional,
                functionalCurrency,
              )}
            </dd>
          </div>
        )}
      </dl>
    </Card>
  );
}

function SummaryCard({
  title,
  rows,
  testId,
  footer,
}: {
  title: string;
  /**
   * Each row carries a primary `value` (functional currency, displayed
   * boldly) and optionally a `native` sub-value (contract currency,
   * displayed smaller + muted underneath). When `native` is omitted (or
   * equals value verbatim — single-currency case), no sub-line renders.
   * MINI_FIX_CS: this is how the on-screen ÖZET surfaces the paired
   * functional + native figures the spec requires.
   */
  rows: Array<{ label: string; value: string; native?: string }>;
  testId: string;
  /**
   * MINI_FIX_CP — optional advisory rendered inside the card after the
   * row list. Used by the maturity card to attach the FX ST/LT
   * translation disclosure note for FX contracts; unused elsewhere.
   */
  footer?: React.ReactNode;
}) {
  return (
    <Card className="p-5 glow-card" data-testid={testId}>
      <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
        {title}
      </h4>
      <dl className="space-y-1.5 text-sm">
        {rows.map((r) => (
          <div
            key={r.label}
            className="flex justify-between items-baseline gap-3"
          >
            <dt className="text-muted-foreground">{r.label}</dt>
            <dd className="text-right">
              <div className="font-mono tabular-nums font-medium">
                {r.value}
              </div>
              {r.native !== undefined && r.native !== r.value && (
                <div
                  className="font-mono tabular-nums text-xs text-muted-foreground"
                  data-testid={`${testId}-native-${r.label}`}
                >
                  {r.native}
                </div>
              )}
            </dd>
          </div>
        ))}
      </dl>
      {footer}
    </Card>
  );
}

// ─── Section 2: Amortization Schedule ─────────────────────────────────────

interface AmortizationSectionProps {
  schedule: SchedulePeriod[];
  currency: string;
  functionalCurrency: string;
  isFxContract: boolean;
  auditPeriodStart: string;
  auditPeriodEnd: string;
  /**
   * Phase 6a.20c — when the contract was terminated, this is the
   * termination date (ISO). Drives the schedule-truncation note above
   * the table so auditors don't think the shorter schedule is a data
   * problem. Undefined for contracts that ran to natural maturity.
   */
  terminationDate?: string;
}

type CurrencyView = "contract" | "functional";

function AmortizationSection({
  schedule,
  currency,
  functionalCurrency,
  isFxContract,
  auditPeriodStart,
  auditPeriodEnd,
  terminationDate,
}: AmortizationSectionProps) {
  const { t } = useTranslation();
  const [auditOnly, setAuditOnly] = React.useState(true);
  // Mini-fix BA — second toggle alongside the audit-period filter. For
  // FX contracts the auditor can flip the body between the 12 contract-
  // currency columns (default) and 12 functional-currency columns
  // surfacing Phase 6c.1b backend fields. Non-FX contracts hide the
  // toggle entirely (single currency, no meaningful choice).
  const [currencyView, setCurrencyView] =
    React.useState<CurrencyView>("contract");

  const filtered = React.useMemo(() => {
    if (!auditOnly) return schedule;
    const start = parseISO(auditPeriodStart);
    const end = parseISO(auditPeriodEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return schedule;
    }
    return schedule.filter((row) => {
      const d = parseISO(row.date);
      return isWithinInterval(d, { start, end });
    });
  }, [schedule, auditOnly, auditPeriodStart, auditPeriodEnd]);

  const totals = React.useMemo(() => {
    // MINI_FIX_H9 — accumulate the 8 amortization-footer money columns with
    // Decimal (not float) so the footer total stays decimal-exact against the
    // per-row backend toFixed(10) inputs. The footer JSX calls
    // `totals.X.toFixed(2)` which works identically on Decimal (returns a
    // string), so no downstream change is needed.
    let interest = new Decimal(0);
    let payment = new Decimal(0);
    let principal = new Decimal(0);
    let depreciation = new Decimal(0);
    let interestFunctional = new Decimal(0);
    let paymentFunctional = new Decimal(0);
    let fxGainLoss = new Decimal(0);
    let depreciationFunctional = new Decimal(0);
    for (const r of filtered) {
      interest = interest.plus(decSafe(r.interestExpense));
      payment = payment.plus(decSafe(r.payment));
      principal = principal.plus(decSafe(r.principalRepayment));
      depreciation = depreciation.plus(decSafe(r.depreciation));
      interestFunctional = interestFunctional.plus(
        decSafe(r.interestExpenseFunctional),
      );
      paymentFunctional = paymentFunctional.plus(
        decSafe(r.paymentFunctional),
      );
      fxGainLoss = fxGainLoss.plus(decSafe(r.fxGainLoss));
      depreciationFunctional = depreciationFunctional.plus(
        decSafe(r.depreciationFunctional),
      );
    }
    return {
      interest,
      payment,
      principal,
      depreciation,
      interestFunctional,
      paymentFunctional,
      fxGainLoss,
      depreciationFunctional,
    };
  }, [filtered]);

  if (schedule.length === 0) {
    return (
      <Card className="p-8" data-testid="amortization-empty">
        <p className="text-sm italic text-muted-foreground text-center">
          {t("contractDetail.amortization.empty")}
        </p>
      </Card>
    );
  }

  const view: CurrencyView = isFxContract ? currencyView : "contract";

  return (
    <Card className="p-4 glow-card" data-testid="contract-detail-amortization">
      {terminationDate && (
        <p
          className="mb-3 text-xs italic text-muted-foreground rounded border-l-2 border-amber-400 bg-amber-50 dark:bg-amber-950/20 px-3 py-2"
          data-testid="amortization-termination-note"
        >
          {t("engagement.contract.termination.scheduleNote", {
            date: terminationDate,
          })}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={auditOnly}
            onCheckedChange={(c) => setAuditOnly(c === true)}
            id="amort-filter"
            data-testid="amortization-filter-switch"
          />
          <Label htmlFor="amort-filter" className="text-sm">
            {auditOnly
              ? t("contractDetail.amortization.filter.auditPeriod")
              : t("contractDetail.amortization.filter.allPeriods")}
          </Label>
        </div>
        {isFxContract && (
          <RadioGroup
            value={currencyView}
            onValueChange={(v) => setCurrencyView(v as CurrencyView)}
            className="flex w-auto items-center gap-3"
            data-testid="amortization-currency-toggle"
          >
            <span className="text-sm text-muted-foreground">
              💱 {t("contractDetail.amortization.currencyView.label")}
            </span>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                value="contract"
                id="ccy-contract"
                data-testid="amortization-currency-contract"
              />
              <Label htmlFor="ccy-contract" className="cursor-pointer text-sm">
                {currency}
              </Label>
            </div>
            <div className="flex items-center gap-1.5">
              <RadioGroupItem
                value="functional"
                id="ccy-functional"
                data-testid="amortization-currency-functional"
              />
              <Label
                htmlFor="ccy-functional"
                className="cursor-pointer text-sm"
              >
                {functionalCurrency}
              </Label>
            </div>
          </RadioGroup>
        )}
        <p
          className="text-sm text-muted-foreground"
          data-testid="amortization-viewing"
        >
          {t("contractDetail.amortization.viewing", {
            shown: filtered.length,
            total: schedule.length,
          })}
        </p>
      </div>

      <div className="overflow-x-auto w-full">
        <Table className="text-xs min-w-max" data-testid="amortization-table">
          <TableHeader>
            {view === "contract" ? (
              <TableRow data-testid="amortization-header-contract">
                <TableHead className="font-mono">
                  {t("contractDetail.amortization.column.period")}
                </TableHead>
                <TableHead className="font-mono">
                  {t("contractDetail.amortization.column.date")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.openingLiability")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.interest")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.payment")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.principal")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.closingLiability")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.openingROU")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.depreciation")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.closingROU")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.cumulativeInterest")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.cumulativePrincipal")}
                </TableHead>
              </TableRow>
            ) : (
              <TableRow data-testid="amortization-header-functional">
                <TableHead className="font-mono">
                  {t("contractDetail.amortization.column.period")}
                </TableHead>
                <TableHead className="font-mono">
                  {t("contractDetail.amortization.column.date")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.periodEndRate")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.paymentDateRate")}
                </TableHead>
                <TableHead className="text-right">
                  {t(
                    "contractDetail.amortization.column.openingLiabilityFunctional",
                    { currency: functionalCurrency },
                  )}
                </TableHead>
                <TableHead className="text-right">
                  {t(
                    "contractDetail.amortization.column.interestFunctional",
                    { currency: functionalCurrency },
                  )}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.paymentFunctional", {
                    currency: functionalCurrency,
                  })}
                </TableHead>
                <TableHead className="text-right">
                  {t(
                    "contractDetail.amortization.column.closingLiabilityFunctional",
                    { currency: functionalCurrency },
                  )}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.amortization.column.fxGainLoss", {
                    currency: functionalCurrency,
                  })}
                </TableHead>
                <TableHead className="text-right">
                  {t(
                    "contractDetail.amortization.column.openingROUFunctional",
                    { currency: functionalCurrency },
                  )}
                </TableHead>
                <TableHead className="text-right">
                  {t(
                    "contractDetail.amortization.column.depreciationFunctional",
                    { currency: functionalCurrency },
                  )}
                </TableHead>
                <TableHead className="text-right">
                  {t(
                    "contractDetail.amortization.column.closingROUFunctional",
                    { currency: functionalCurrency },
                  )}
                </TableHead>
              </TableRow>
            )}
          </TableHeader>
          <TableBody>
            {filtered.map((row) => (
              <TableRow
                key={row.period}
                data-testid={`amortization-row-${row.period}`}
                className={cn(
                  // Mini-fix AW — proforma rows (post-audit-period
                  // projections translated at the engagement-level
                  // closing rate) get a muted background + italic so
                  // the auditor never mistakes them for booked values.
                  row.isProforma && "bg-muted/40 text-muted-foreground italic",
                )}
              >
                <TableCell className="font-mono">{row.period}</TableCell>
                <TableCell className="font-mono">
                  {Number.isNaN(parseISO(row.date).getTime())
                    ? row.date
                    : format(parseISO(row.date), "dd.MM.yyyy")}
                  {row.isProforma && (
                    <span
                      className="ml-2 inline-flex items-center rounded bg-amber-500/20 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                      data-testid={`amortization-row-${row.period}-proforma-badge`}
                      title={t("contractDetail.amortization.proformaTooltip")}
                    >
                      {t("contractDetail.amortization.proformaBadge")}
                    </span>
                  )}
                </TableCell>
                {view === "contract" ? (
                  <>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.openingLiability, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.interestExpense, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.payment, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.principalRepayment, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.closingLiability, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.openingROU, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.depreciation, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.closingROU, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.cumulativeInterest, currency)}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(row.cumulativePrincipal, currency)}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.periodEndRate !== undefined
                        ? formatNumber(row.periodEndRate, 4)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.paymentDateRateUsed !== undefined
                        ? formatNumber(row.paymentDateRateUsed, 4)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.openingLiabilityFunctional !== undefined
                        ? formatCurrency(
                            row.openingLiabilityFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.interestExpenseFunctional !== undefined
                        ? formatCurrency(
                            row.interestExpenseFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.paymentFunctional !== undefined
                        ? formatCurrency(
                            row.paymentFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.closingLiabilityFunctional !== undefined
                        ? formatCurrency(
                            row.closingLiabilityFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.fxGainLoss !== undefined
                        ? formatCurrency(row.fxGainLoss, functionalCurrency)
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.openingROUFunctional !== undefined
                        ? formatCurrency(
                            row.openingROUFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.depreciationFunctional !== undefined
                        ? formatCurrency(
                            row.depreciationFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {row.closingROUFunctional !== undefined
                        ? formatCurrency(
                            row.closingROUFunctional,
                            functionalCurrency,
                          )
                        : "—"}
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            {view === "contract" ? (
              <TableRow
                className="font-bold border-t-2 bg-muted/30"
                data-testid="amortization-footer"
              >
                <TableCell className="font-bold" colSpan={2}>
                  {t("contractDetail.amortization.row.total")}
                </TableCell>
                <TableCell />
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-interest"
                >
                  {formatCurrency(totals.interest.toFixed(2), currency)}
                </TableCell>
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-payment"
                >
                  {formatCurrency(totals.payment.toFixed(2), currency)}
                </TableCell>
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-principal"
                >
                  {formatCurrency(totals.principal.toFixed(2), currency)}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-depreciation"
                >
                  {formatCurrency(totals.depreciation.toFixed(2), currency)}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
              </TableRow>
            ) : (
              <TableRow
                className="font-bold border-t-2 bg-muted/30"
                data-testid="amortization-footer"
              >
                <TableCell className="font-bold" colSpan={2}>
                  {t("contractDetail.amortization.row.total")}
                </TableCell>
                {/* Rate columns + opening balance — no aggregate meaning */}
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-interest-functional"
                >
                  {formatCurrency(
                    totals.interestFunctional.toFixed(2),
                    functionalCurrency,
                  )}
                </TableCell>
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-payment-functional"
                >
                  {formatCurrency(
                    totals.paymentFunctional.toFixed(2),
                    functionalCurrency,
                  )}
                </TableCell>
                <TableCell />
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-fx-gain-loss"
                >
                  {formatCurrency(
                    totals.fxGainLoss.toFixed(2),
                    functionalCurrency,
                  )}
                </TableCell>
                <TableCell />
                <TableCell
                  className="text-right font-mono tabular-nums"
                  data-testid="amortization-footer-depreciation-functional"
                >
                  {formatCurrency(
                    totals.depreciationFunctional.toFixed(2),
                    functionalCurrency,
                  )}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableFooter>
        </Table>
      </div>
    </Card>
  );
}

// ─── Section 3: Journal Entries (per-contract) ─────────────────────────────

interface JESectionProps {
  journalEntries: JournalEntry[];
  contractId: string;
  currency: string;
  auditPeriodStart: string;
  auditPeriodEnd: string;
}

function JESection({
  journalEntries,
  contractId,
  currency,
  auditPeriodStart,
  auditPeriodEnd,
}: JESectionProps) {
  const { t } = useTranslation();
  const [periodFilter, setPeriodFilter] = React.useState<
    "AUDIT_PERIOD" | "ALL_PERIODS"
  >("AUDIT_PERIOD");
  const [typeFilter, setTypeFilter] = React.useState<"ALL" | JournalEntryType>(
    "ALL",
  );

  // Phase 6a.18 — adapt the per-contract `JournalEntry` shape (uses
  // `lines[].accountName`) into the `AggregatedJournalEntry` shape that
  // the shared `JECard`/`MonthGroup` consume (uses `lines[].account`).
  const aggregated: AggregatedJournalEntry[] = React.useMemo(
    () =>
      journalEntries.map((je) => ({
        period: je.date,
        type: je.type,
        lines: je.lines.map((l) => ({
          account: l.accountName,
          debit: l.debit,
          credit: l.credit,
        })),
        contractIds: [contractId],
        contractCount: 1,
      })),
    [journalEntries, contractId],
  );

  const filtered = React.useMemo(() => {
    if (aggregated.length === 0) return [];
    const start = parseISO(auditPeriodStart);
    const end = parseISO(auditPeriodEnd);
    return aggregated.filter((je) => {
      if (periodFilter === "AUDIT_PERIOD") {
        const d = parseISO(je.period);
        if (
          !Number.isNaN(d.getTime()) &&
          !isWithinInterval(d, { start, end })
        ) {
          return false;
        }
      }
      if (typeFilter !== "ALL" && je.type !== typeFilter) return false;
      return true;
    });
  }, [aggregated, periodFilter, typeFilter, auditPeriodStart, auditPeriodEnd]);

  const groupedByMonth = React.useMemo(() => {
    const groups = new Map<string, AggregatedJournalEntry[]>();
    for (const je of filtered) {
      const key = format(parseISO(je.period), "yyyy-MM");
      const bucket = groups.get(key);
      if (bucket) bucket.push(je);
      else groups.set(key, [je]);
    }
    return Array.from(groups.entries()).sort(([a], [b]) =>
      a.localeCompare(b),
    );
  }, [filtered]);

  if (journalEntries.length === 0) {
    return (
      <Card className="p-8" data-testid="je-empty">
        <p className="text-sm italic text-muted-foreground text-center">
          {t("contractDetail.je.empty")}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="contract-detail-je">
      {/* BB3 — same Yıllık Konsolide Özet panel as the engagement-level
          Yevmiye tab, scoped to this contract's filtered entries. */}
      <JournalYearlySummary
        entries={filtered}
        currency={currency}
        testIdPrefix="contract-je-yearly-summary"
      />
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h3 className="text-base font-semibold">
              {t("contractDetail.je.section.title", { contractId })}
            </h3>
            <p
              className="text-sm text-muted-foreground"
              data-testid="je-viewing"
            >
              {t("contractDetail.je.viewing", {
                shown: filtered.length,
                total: aggregated.length,
              })}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">
              {t("je.filter.period.label")}
            </Label>
            <Select
              value={periodFilter}
              onValueChange={(v) =>
                setPeriodFilter(v as "AUDIT_PERIOD" | "ALL_PERIODS")
              }
            >
              <SelectTrigger
                className="w-[200px]"
                data-testid="je-filter-period"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AUDIT_PERIOD">
                  {t("je.filter.period.auditPeriod", {
                    year: new Date(auditPeriodStart).getFullYear(),
                  })}
                </SelectItem>
                <SelectItem value="ALL_PERIODS">
                  {t("je.filter.period.allPeriods")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground mb-1 block">
              {t("je.filter.type.label")}
            </Label>
            <Select
              value={typeFilter}
              onValueChange={(v) =>
                setTypeFilter(v as "ALL" | JournalEntryType)
              }
            >
              <SelectTrigger
                className="w-[260px]"
                data-testid="je-filter-type"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">
                  {t("contracts.toolbar.complexity.all")}
                </SelectItem>
                {PER_CONTRACT_JE_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`je.type.${type}` as never) as string}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-8" data-testid="je-empty-filtered">
          <p className="text-sm italic text-muted-foreground text-center">
            {t("contractDetail.je.empty")}
          </p>
        </Card>
      ) : (
        groupedByMonth.map(([monthKey, monthJes], idx) => {
          let offset = 0;
          for (let i = 0; i < idx; i++) offset += groupedByMonth[i]![1].length;
          return (
            <MonthGroup
              key={monthKey}
              monthKey={monthKey}
              jes={monthJes}
              currency={currency}
              indexOffset={offset}
              showMonthlySummary={false}
              showPerContractPlaceholder={false}
            />
          );
        })
      )}
    </div>
  );
}

// ─── Section 4: Metadata (flags + provenance) ──────────────────────────────

interface MetadataSectionProps {
  contract: CalculationResult;
  contractSummary:
    | {
        isMultiCurrency: boolean;
        hasModifications: boolean;
        hasTermination: boolean;
        hasIndexLinkage: boolean;
        hasPriorReconciliation: boolean;
      }
    | undefined;
}

function MetadataSection({
  contract,
  contractSummary,
}: MetadataSectionProps) {
  const { t } = useTranslation();
  const calculatedAt = parseISO(contract.calculatedAt);
  const calculatedAtDisplay = Number.isNaN(calculatedAt.getTime())
    ? contract.calculatedAt
    : format(calculatedAt, "dd.MM.yyyy HH:mm:ss");
  const monthlyPct = (Number(contract.periodicRateMonthly) || 0) * 100;

  const flagRows = contractSummary
    ? [
        {
          key: "multiCurrency",
          emoji: "🌐",
          label: t("contracts.flag.multiCurrency"),
          active: contractSummary.isMultiCurrency,
        },
        {
          key: "modification",
          emoji: "✏️",
          label: t("contracts.flag.modification"),
          active: contractSummary.hasModifications,
        },
        {
          key: "termination",
          emoji: "⏹",
          label: t("contracts.flag.termination"),
          active: contractSummary.hasTermination,
        },
        {
          key: "indexLinkage",
          emoji: "📊",
          label: t("contracts.flag.indexLinkage"),
          active: contractSummary.hasIndexLinkage,
        },
        {
          key: "priorReconciliation",
          emoji: "🔄",
          label: t("contracts.flag.priorReconciliation"),
          active: contractSummary.hasPriorReconciliation,
        },
      ]
    : [];

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="contract-detail-metadata"
    >
      <Card className="p-5 glow-card" data-testid="metadata-flags">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          {t("contractDetail.metadata.flags.title")}
        </h4>
        <div className="space-y-2">
          {flagRows.map((f) => (
            <div
              key={f.key}
              className={cn(
                "flex items-center justify-between gap-3 p-2 rounded-md border",
                f.active
                  ? "bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-700"
                  : "bg-muted/30 border-border text-muted-foreground",
              )}
              data-testid={`metadata-flag-${f.key}`}
              data-active={f.active ? "true" : "false"}
            >
              <span className="flex items-center gap-2 text-sm">
                <span className="text-lg">{f.emoji}</span> {f.label}
              </span>
              <span
                className={cn(
                  "text-xs font-medium px-2 py-0.5 rounded",
                  f.active
                    ? "bg-emerald-200/60 dark:bg-emerald-800/60 text-emerald-900 dark:text-emerald-100"
                    : "bg-muted text-muted-foreground",
                )}
              >
                {f.active
                  ? t("contractDetail.metadata.flags.active")
                  : t("contractDetail.metadata.flags.inactive")}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 glow-card" data-testid="metadata-provenance">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          {t("contractDetail.metadata.provenance.title")}
        </h4>
        <dl className="space-y-1.5 text-sm">
          <ProvenanceRow
            label={t("contractDetail.metadata.provenance.calculatedAt")}
            value={calculatedAtDisplay}
          />
          <ProvenanceRow
            label={t("contractDetail.metadata.provenance.toolVersion")}
            value={contract.toolVersion}
          />
          <ProvenanceRow
            label={t("contractDetail.metadata.provenance.contractCurrency")}
            value={contract.currency}
          />
          <ProvenanceRow
            label={t("contractDetail.metadata.provenance.functionalCurrency")}
            value={contract.functionalCurrency}
          />
          <ProvenanceRow
            label={t("contractDetail.metadata.provenance.isFxContract")}
            value={
              contract.isFxContract
                ? t("contractDetail.metadata.yes")
                : t("contractDetail.metadata.no")
            }
          />
          <ProvenanceRow
            label={t("contractDetail.metadata.provenance.monthlyRate")}
            value={`%${monthlyPct.toFixed(4)}`}
          />
        </dl>
      </Card>
    </div>
  );
}

function ProvenanceRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-baseline gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono tabular-nums font-medium">{value}</dd>
    </div>
  );
}

// ─── Section 5: Contract Events (ROU layers + event JEs) ───────────────────

interface EventsSectionProps {
  rouLayers: ROULayer[];
  journalEntries: JournalEntry[];
  currency: string;
}

function EventsSection({
  rouLayers,
  journalEntries,
  currency,
}: EventsSectionProps) {
  const { t } = useTranslation();
  const eventJEs = journalEntries.filter((je) => EVENT_TYPES.has(je.type));
  const hasEvents = eventJEs.length > 0;

  return (
    <div className="space-y-4" data-testid="contract-detail-events">
      <Card className="p-4 glow-card" data-testid="events-layers">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          {t("contractDetail.events.layers.title")}
        </h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-mono w-12">
                {t("contractDetail.events.layers.column.index")}
              </TableHead>
              <TableHead>
                {t("contractDetail.events.layers.column.addedDate")}
              </TableHead>
              <TableHead className="text-right">
                {t("contractDetail.events.layers.column.originalAmount")}
              </TableHead>
              <TableHead className="text-right">
                {t("contractDetail.events.layers.column.historicRate")}
              </TableHead>
              <TableHead className="text-right">
                {t("contractDetail.events.layers.column.functionalAmount")}
              </TableHead>
              <TableHead className="text-right">
                {t("contractDetail.events.layers.column.remainingMonths")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rouLayers.map((layer, idx) => (
              <TableRow
                key={`${layer.addedDate}-${idx}`}
                data-testid={`events-layer-${idx + 1}`}
              >
                <TableCell className="font-mono">{idx + 1}</TableCell>
                <TableCell>
                  {Number.isNaN(parseISO(layer.addedDate).getTime())
                    ? layer.addedDate
                    : format(parseISO(layer.addedDate), "dd.MM.yyyy")}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatCurrency(layer.originalContractAmount, currency)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {layer.historicRate}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {formatCurrency(layer.functionalAmount)}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">
                  {layer.remainingDepreciableMonths}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-4 glow-card" data-testid="events-event-jes">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
          {t("contractDetail.events.eventJEs.title")}
        </h4>
        {!hasEvents ? (
          // Phase BB5-6 — slightly richer empty state: icon + softened copy.
          <div
            className="flex flex-col items-center justify-center gap-2 py-4 text-center text-sm italic text-muted-foreground"
            data-testid="events-empty"
          >
            <CheckCircle2
              className="h-5 w-5 text-muted-foreground/70"
              aria-hidden="true"
            />
            <p>{t("contractDetail.events.empty")}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("contractDetail.events.eventJEs.column.date")}
                </TableHead>
                <TableHead>
                  {t("contractDetail.events.eventJEs.column.type")}
                </TableHead>
                <TableHead>
                  {t("contractDetail.events.eventJEs.column.description")}
                </TableHead>
                <TableHead className="text-right">
                  {t("contractDetail.events.eventJEs.column.amount")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {eventJEs.map((je) => {
                // MINI_FIX_H9 — sum JE-line debits with Decimal (not float)
                // so the amount column matches the decimal-exact total of the
                // backend-supplied per-line debits.
                const debitSum = je.lines.reduce(
                  (acc, l) => acc.plus(decSafe(l.debit)),
                  new Decimal(0),
                );
                return (
                  <TableRow
                    key={je.id}
                    data-testid={`events-event-${je.id}`}
                  >
                    <TableCell className="font-mono">
                      {Number.isNaN(parseISO(je.date).getTime())
                        ? je.date
                        : format(parseISO(je.date), "dd.MM.yyyy")}
                    </TableCell>
                    <TableCell>
                      {t(`je.type.${je.type}` as never) as string}
                    </TableCell>
                    <TableCell className="text-sm">{je.description}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {formatCurrency(debitSum.toFixed(2), currency)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
